import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Tables } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { CurrencyInput } from '@/components/ui/currency-input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { Loader2, AlertCircle } from 'lucide-react'
import { checkPriceAdjustmentOverlap } from '@/lib/pricing/overlap-validation'

type PriceAdjustment = Tables<'price_adjustments'>

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
] as const

// Schema for form validation
const priceAdjustmentSchema = z
  .object({
    name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
    description: z.string().max(500, 'Description too long').optional(),
    start_date: z.string().optional(),
    end_date: z.string().optional(),
    start_time: z.string().regex(/^\d{2}:\d{2}$/, 'Invalid time format'),
    end_time: z.string().regex(/^\d{2}:\d{2}$/, 'Invalid time format'),
    days_of_week: z
      .array(z.number())
      .min(1, 'Select at least one day')
      .max(7),
    adjustment_type: z.enum(['increase', 'decrease']),
    amount_type: z.enum(['percentage', 'flat']),
    // Single magnitude field; maps to percentage_increase or flat_amount on save.
    value: z.coerce.number().min(0, 'Must be 0 or greater'),
    is_active: z.boolean().default(true),
    apply_to_all_vehicles: z.boolean().default(true),
    vehicle_ids: z.array(z.string()).optional(),
  })
  .refine((data) => data.start_time !== data.end_time, {
    message: 'Start and end time must be different',
    path: ['end_time'],
  })
  .superRefine((data, ctx) => {
    if (data.amount_type === 'percentage') {
      // A discount can't exceed 100%; a surge is capped at 500%.
      const max = data.adjustment_type === 'decrease' ? 100 : 500
      if (data.value > max) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Maximum ${max}%`,
          path: ['value'],
        })
      }
    } else if (data.value <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter an amount greater than 0',
        path: ['value'],
      })
    }
  })

type PriceAdjustmentFormData = z.infer<typeof priceAdjustmentSchema>

interface PriceAdjustmentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  adjustment?: PriceAdjustment | null
  accountId: string
  vehicles: Array<{ id: string; name: string }>
  existingAdjustments?: PriceAdjustment[]
  onSuccess?: () => void
}

export function PriceAdjustmentDialog({
  open,
  onOpenChange,
  adjustment,
  accountId,
  vehicles,
  existingAdjustments = [],
  onSuccess,
}: PriceAdjustmentDialogProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const isEditing = !!adjustment
  const [validationError, setValidationError] = useState<string | null>(null)

  const form = useForm<PriceAdjustmentFormData>({
    resolver: zodResolver(priceAdjustmentSchema),
    defaultValues: {
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      start_time: '18:00',
      end_time: '23:00',
      days_of_week: [5, 6], // Friday, Saturday default
      adjustment_type: 'increase',
      amount_type: 'percentage',
      value: 20,
      is_active: true,
      apply_to_all_vehicles: true,
      vehicle_ids: [],
    },
  })

  // Reset form when dialog opens or adjustment changes
  useEffect(() => {
    if (open) {
      setValidationError(null)
      if (adjustment) {
        const amountType = adjustment.amount_type === 'flat' ? 'flat' : 'percentage'
        form.reset({
          name: adjustment.name,
          description: adjustment.description || '',
          start_date: adjustment.start_date || '',
          end_date: adjustment.end_date || '',
          start_time: adjustment.start_time.slice(0, 5), // "HH:MM:SS" -> "HH:MM"
          end_time: adjustment.end_time.slice(0, 5),
          days_of_week: adjustment.days_of_week,
          adjustment_type: adjustment.adjustment_type === 'decrease' ? 'decrease' : 'increase',
          amount_type: amountType,
          value:
            amountType === 'flat'
              ? adjustment.flat_amount ?? 0
              : adjustment.percentage_increase,
          is_active: adjustment.is_active,
          apply_to_all_vehicles: !adjustment.vehicle_ids,
          vehicle_ids: adjustment.vehicle_ids || [],
        })
      } else {
        form.reset({
          name: '',
          description: '',
          start_date: '',
          end_date: '',
          start_time: '18:00',
          end_time: '23:00',
          days_of_week: [5, 6],
          adjustment_type: 'increase',
          amount_type: 'percentage',
          value: 20,
          is_active: true,
          apply_to_all_vehicles: true,
          vehicle_ids: [],
        })
      }
    }
  }, [open, adjustment, form])

  // Mutation for create/update
  const mutation = useMutation({
    mutationFn: async (data: PriceAdjustmentFormData) => {
      const vehicleIds = data.apply_to_all_vehicles ? null : (data.vehicle_ids || [])

      // Prevent clashing adjustments: two active rules that overlap on
      // day/time/vehicles are ambiguous (the engine can only apply one).
      // An inactive rule can't clash, so only enforce for active ones.
      // Note: minimum hour rules are NOT checked — they stack with adjustments
      // (minimum hours set the base charge, the adjustment applies on top), so
      // overlapping with one is expected and allowed.
      if (data.is_active && existingAdjustments.length > 0) {
        const adjustmentConflict = checkPriceAdjustmentOverlap(
          {
            id: adjustment?.id || '',
            name: data.name,
            daysOfWeek: data.days_of_week,
            startTime: data.start_time,
            endTime: data.end_time,
            vehicleIds,
            isActive: data.is_active,
            percentageIncrease: data.amount_type === 'percentage' ? data.value : 0,
          },
          existingAdjustments.map((a) => ({
            id: a.id,
            name: a.name,
            daysOfWeek: a.days_of_week,
            startTime: a.start_time.slice(0, 5),
            endTime: a.end_time.slice(0, 5),
            vehicleIds: a.vehicle_ids,
            isActive: a.is_active,
            percentageIncrease: a.percentage_increase,
          })),
          adjustment?.id
        )

        if (adjustmentConflict.hasConflict) {
          throw new Error(adjustmentConflict.conflictDetails)
        }
      }

      const isFlat = data.amount_type === 'flat'
      const payload = {
        account_id: accountId,
        name: data.name,
        description: data.description || null,
        start_date: data.start_date || null,
        end_date: data.end_date || null,
        start_time: data.start_time + ':00',
        end_time: data.end_time + ':00',
        days_of_week: data.days_of_week,
        adjustment_type: data.adjustment_type,
        amount_type: data.amount_type,
        // percentage_increase is NOT NULL in the DB; store 0 for flat rules.
        percentage_increase: isFlat ? 0 : data.value,
        flat_amount: isFlat ? data.value : null,
        is_active: data.is_active,
        vehicle_ids: vehicleIds,
      }

      if (isEditing && adjustment) {
        const { error } = await supabase
          .from('price_adjustments')
          .update(payload)
          .eq('id', adjustment.id)

        if (error) throw error
      } else {
        const { error } = await supabase.from('price_adjustments').insert(payload)

        if (error) throw error
      }
    },
    onSuccess: () => {
      setValidationError(null)
      queryClient.invalidateQueries({ queryKey: ['price-adjustments', accountId] })
      toast({
        title: isEditing ? 'Adjustment updated' : 'Adjustment created',
        description: `Price adjustment "${form.getValues('name')}" has been ${isEditing ? 'updated' : 'created'}.`,
      })
      onOpenChange(false)
      onSuccess?.()
    },
    onError: (error) => {
      setValidationError(error.message)
    },
  })

  const selectedDays = form.watch('days_of_week')
  const applyToAllVehicles = form.watch('apply_to_all_vehicles')
  const adjustmentType = form.watch('adjustment_type')
  const amountType = form.watch('amount_type')
  const isDiscount = adjustmentType === 'decrease'

  const handleDayToggle = (day: number, checked: boolean) => {
    const current = form.getValues('days_of_week')
    if (checked) {
      form.setValue('days_of_week', [...current, day].sort((a, b) => a - b), {
        shouldValidate: true,
      })
    } else {
      form.setValue(
        'days_of_week',
        current.filter((d) => d !== day),
        { shouldValidate: true }
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Price Adjustment' : 'Add Price Adjustment'}
          </DialogTitle>
          <DialogDescription>
            Configure a time-based price adjustment rule. Increase prices (surge)
            or reduce them (discount) during specified time windows.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((data) => {
              setValidationError(null)
              mutation.mutate(data)
            })}
            className="space-y-4"
          >
            {validationError && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{validationError}</span>
              </div>
            )}

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Weekend Peak Hours" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="e.g., Higher demand on weekend evenings"
                      className="resize-none"
                      rows={2}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Time</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Time</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Can be before start for overnight
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="days_of_week"
              render={() => (
                <FormItem>
                  <FormLabel>Days of Week</FormLabel>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map((day) => (
                      <label
                        key={day.value}
                        className="flex items-center gap-1.5 cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedDays.includes(day.value)}
                          onCheckedChange={(checked) =>
                            handleDayToggle(day.value, checked === true)
                          }
                        />
                        <span className="text-sm">{day.label}</span>
                      </label>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date (optional)</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Leave empty for recurring
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date (optional)</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="adjustment_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Adjustment Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="increase">Increase (surge)</SelectItem>
                        <SelectItem value="decrease">Discount</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="amount_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Value Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select value type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage (%)</SelectItem>
                        <SelectItem value="flat">Flat Amount ($)</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {isDiscount ? 'Discount' : 'Increase'}{' '}
                    {amountType === 'flat' ? 'Amount ($)' : '(%)'}
                  </FormLabel>
                  <FormControl>
                    {amountType === 'flat' ? (
                      <CurrencyInput
                        value={field.value}
                        onChange={(val) => field.onChange(val ?? 0)}
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={0}
                          max={isDiscount ? 100 : 500}
                          step={1}
                          className="w-24"
                          {...field}
                        />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    )}
                  </FormControl>
                  <FormDescription>
                    {isDiscount
                      ? amountType === 'flat'
                        ? 'Fixed dollar amount taken off the subtotal during this window'
                        : 'Percentage taken off the subtotal during this window'
                      : amountType === 'flat'
                        ? 'Fixed dollar amount added to the subtotal during this window'
                        : 'Percentage added to the subtotal during this window'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Vehicle Selection */}
            <FormField
              control={form.control}
              name="apply_to_all_vehicles"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Apply to All Vehicles</FormLabel>
                    <FormDescription className="text-xs">
                      Or select specific vehicles this adjustment applies to
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {!applyToAllVehicles && (
              <FormField
                control={form.control}
                name="vehicle_ids"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Select Vehicles</FormLabel>
                    <div className="border rounded-lg p-3 max-h-[150px] overflow-y-auto space-y-2">
                      {vehicles.length > 0 ? (
                        vehicles.map((vehicle) => (
                          <div key={vehicle.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`vehicle-${vehicle.id}`}
                              checked={field.value?.includes(vehicle.id)}
                              onCheckedChange={(checked) => {
                                const newValue = checked
                                  ? [...(field.value || []), vehicle.id]
                                  : (field.value || []).filter((id) => id !== vehicle.id)
                                field.onChange(newValue)
                              }}
                            />
                            <label
                              htmlFor={`vehicle-${vehicle.id}`}
                              className="text-sm font-medium leading-none cursor-pointer"
                            >
                              {vehicle.name}
                            </label>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No vehicles available
                        </p>
                      )}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Active</FormLabel>
                    <FormDescription className="text-xs">
                      Enable this adjustment rule
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isEditing ? 'Save Changes' : 'Create Adjustment'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
